/**
 * "Suggest criteria" on the Testing tab's Criteria section (task 1186).
 *
 * The click IS the request (task 1196): there is nothing for the User to add,
 * so instead of placing a draft in the input it opens the Assistant sidebar
 * and sends `SUGGEST_CRITERIA_PROMPT` as the User's own first turn of a NEW thread through `seedFirstTurn` (task 1117).
 * The turn carries the service as its resource, so the thread files under it,
 * and the Assistant's dispatcher routes it to the Criteria Composer (task
 * 1187), which reads the service and saves its suggestions as criteria with
 * source `generated`. Rendered once: primary in the empty state, secondary in
 * the section header once criteria exist.
 *
 * So the User sees the request travel, the button settles into a sent state
 * for about a second (check icon, "Sent to Assistant", disabled) and asks the
 * composer to pulse once (`pulseComposer`). That behavior lives in the shared
 * `useAssistantHandoff` hook (task 1200), which Generate tests and Create an
 * interface use too. Analytics are unchanged: the send fires
 * `help_assistant_asked` exactly as the drafted turn did when the User pressed
 * Enter, so the metric does not split between the two behaviors.
 *
 * With no Assistant provider mounted there is nothing to open, so it renders
 * nothing (the `CreateInterfaceButton` rule); Add by hand stays.
 */
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useAssistantHandoff } from '@/hooks/useAssistantHandoff';

/** The one prompt. Exported so the Assistant sidebar's criteria-turn card (task 1188) can reference it. */
export const SUGGEST_CRITERIA_PROMPT = 'Suggest criteria for this AI Service';

const PLACEMENT_CLASS = {
  empty:
    'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:cursor-default disabled:hover:bg-indigo-600',
  header:
    'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-white dark:bg-gray-700 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-gray-600 disabled:cursor-default disabled:hover:bg-white dark:disabled:hover:bg-gray-700',
} as const;

export function SuggestCriteriaButton({ placement }: { placement: 'empty' | 'header' }) {
  // Off, the click places the prompt in the composer unsent (task 1215).
  const autoSubmit = useFlag('innovate.fe.testing.assistant-handoff');
  const { assistant, sent, handoff } = useAssistantHandoff(SUGGEST_CRITERIA_PROMPT, { autoSubmit });

  if (!assistant) return null;

  const iconClass = `${placement === 'empty' ? 'h-4 w-4 mr-2' : 'h-4 w-4 mr-1.5'} transition-opacity motion-reduce:transition-none`;

  return (
    <button
      type="button"
      data-testid={`criteria-suggest-${placement}`}
      data-sent={sent ? 'true' : undefined}
      onClick={handoff}
      disabled={sent}
      className={`${PLACEMENT_CLASS[placement]} transition-colors motion-reduce:transition-none`}
    >
      {sent ? (
        <CheckIcon data-testid={`criteria-suggest-${placement}-sent-icon`} className={iconClass} aria-hidden="true" />
      ) : (
        <SparklesIcon className={iconClass} aria-hidden="true" />
      )}
      {sent ? 'Sent to Assistant' : 'Suggest criteria'}
    </button>
  );
}

export default SuggestCriteriaButton;
